import { useState } from 'react';
import { ApiError } from '@/api/client';
import { useCreateNomination } from '@/api/queries';
import type { EventCard } from '@/api/types';
import { ChoiceGroup, Switch } from '@/components/kit/Controls';
import { Col, Form, NumberField, Row, TextAreaField, TextField } from '@/components/kit/Field';
import { Modal } from '@/components/kit/Modal';
import { Note } from '@/components/kit/Note';
import { useToast } from '@/components/kit/Toast';
import { mmss } from '@/lib/format';

/**
 * Новая номинация.
 *
 * Полей десять, но обязательных три: название и два хронометража.
 * Лимит команд можно оставить нулевым — его двигают по ходу
 * регистрации, а к публикации он просто должен стать больше нуля,
 * и об этом скажет проверка.
 *
 * Поповеров в окне нет: выбор конкурсности — парный `.pick`,
 * руководитель в составе — тумблер.
 */
export function NominationModal({ event, onClose }: { event: EventCard; onClose: () => void }) {
  const create = useCreateNomination(event.id);
  const toast = useToast();

  const [name, setName] = useState('');
  const [ageFrom, setAgeFrom] = useState<number | null>(null);
  const [ageTo, setAgeTo] = useState<number | null>(null);
  // Значения по умолчанию взяты из типовой номинации: 3:30 лимит,
  // 2:30 среднее. Пустые поля здесь только замедляют работу —
  // их всё равно пришлось бы заполнять теми же числами.
  const [durationLimitSec, setDurationLimitSec] = useState<number | null>(210);
  const [avgDurationSec, setAvgDurationSec] = useState<number | null>(150);
  const [minMembers, setMinMembers] = useState<number | null>(5);
  const [otherAgePct, setOtherAgePct] = useState<number | null>(null);
  const [teamLimit, setTeamLimit] = useState<number | null>(null);
  const [isCompetitive, setIsCompetitive] = useState(true);
  const [leaderOnStage, setLeaderOnStage] = useState(false);
  const [isSolo, setIsSolo] = useState(false);
  const [needVideo, setNeedVideo] = useState(false);
  const [needCostume, setNeedCostume] = useState(false);
  const [description, setDescription] = useState('');

  // «до» не ниже «от» проверяет сервер: формулировок правил на фронтенде нет (ТЗ §5)
  const valid = Boolean(name.trim() && durationLimitSec && avgDurationSec);

  const submit = () =>
    create.mutate(
      {
        name: name.trim(),
        ageFrom,
        ageTo,
        durationLimitSec,
        avgDurationSec,
        minMembers,
        otherAgePct,
        teamLimit: teamLimit ?? 0,
        isCompetitive,
        leaderOnStage,
        isSolo,
        needVideo,
        needCostume,
        description: description.trim() || null,
      },
      {
        onSuccess: () => {
          toast.show(`Номинация «${name.trim()}» заведена`);
          onClose();
        },
        onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не завелась'),
      },
    );

  return (
    <Modal
      title="Новая номинация"
      onClose={onClose}
      size="wide"
      confirm={{ label: 'Завести', onClick: submit, disabled: !valid || create.isPending }}
    >
      <Form>
        <TextField
          label="Название"
          value={name}
          onValue={setName}
          placeholder="BEGINNER 12-15"
        />

        <Row>
          <Col fix>
            <NumberField
              label="Возраст от"
              placeholder="без границы"
              size={12}
              value={ageFrom}
              onValue={setAgeFrom}
            />
          </Col>
          <Col fix>
            <NumberField
              label="Возраст до"
              placeholder="без границы"
              size={12}
              value={ageTo}
              onValue={setAgeTo}
            />
          </Col>
        </Row>

        <Note>
          Границы включительно, пустое поле — без ограничения с этой стороны. По этим числам
          система сверяет даты рождения участников
        </Note>

        <Row>
          <Col>
            <NumberField
              label="Лимит хронометража, сек"
              value={durationLimitSec}
              onValue={setDurationLimitSec}
              hint={durationLimitSec ? mmss(durationLimitSec) : undefined}
            />
          </Col>
          <Col>
            <NumberField
              label="Среднее время номера, сек"
              value={avgDurationSec}
              onValue={setAvgDurationSec}
              hint={avgDurationSec ? mmss(avgDurationSec) : undefined}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <NumberField
              label="Лимит команд"
              placeholder="задам позже"
              value={teamLimit}
              onValue={setTeamLimit}
            />
          </Col>
          <Col>
            <NumberField
              label="Минимальный состав"
              value={minMembers}
              onValue={setMinMembers}
            />
          </Col>
          <Col>
            <NumberField
              label="Процент из другого возраста"
              placeholder="не допускается"
              value={otherAgePct}
              onValue={setOtherAgePct}
            />
          </Col>
        </Row>

        <div className="field">
          <span className="lbl">Определение мест</span>
          <ChoiceGroup
            value={isCompetitive ? 'competitive' : 'showcase'}
            onChange={(v) => setIsCompetitive(v === 'competitive')}
            items={[
              { value: 'competitive', label: 'Конкурсная' },
              { value: 'showcase', label: 'Показательная' },
            ]}
          />
        </div>

        <div className="field">
          <span className="lbl">Руководитель в составе</span>
          <Switch
            checked={leaderOnStage}
            onToggle={() => setLeaderOnStage((v) => !v)}
            label="Может выходить на сцену"
          />
        </div>

        <div className="field">
          <span className="lbl">Регистрация</span>
          <Switch checked={isSolo} onToggle={() => setIsSolo((v) => !v)} label="Сольная — вместо команды выбирают солиста" />
          <Switch checked={needVideo} onToggle={() => setNeedVideo((v) => !v)} label="Нужно видео" />
          <Switch checked={needCostume} onToggle={() => setNeedCostume((v) => !v)} label="Нужны фото костюмов" />
        </div>

        <TextAreaField
          label="Описание"
          rows={3}
          value={description}
          onValue={setDescription}
          placeholder="Видит руководитель при выборе номинации"
        />
      </Form>

      <Note>
        Лимит команд можно не задавать сейчас — но пока он нулевой, мероприятие
        не опубликуется: это одна из двенадцати проверок
      </Note>
    </Modal>
  );
}
